import axios, { AxiosRequestConfig } from 'axios'
import { method } from 'lodash'
import { headers } from 'next/headers'
import { redirect, RedirectType } from 'next/navigation'
import { cache } from 'react'

import { FETCH_RETRY_COUNT, FETCH_RETRY_DELAY } from '@/lib/constants'
import { retryFetch } from '@/lib/fetch-util'
import { commonServerLogger } from '@/lib/logger'
import { sleep } from '@/lib/utils'
import { UserSession } from '@/types/user'

import { getLocale } from './locale-switcher'

interface UseFetchParams {
  fullUrl: string
  headers?: {
    [key: string]: string
  }
  query?: any
  body?: any
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  // reference: https://nextjs.org/docs/app/api-reference/functions/fetch#optionscache
  revalidate?: false | 0 | number
  cache?: 'force-cache' | 'no-store'
  session?: UserSession
  isMultipart?: boolean
  responseFormat?: 'json' | 'text'
  retryCount?: number
}

interface UseAxiosParams {
  fullUrl: string
  config: AxiosRequestConfig
  responseFormat?: 'json' | 'text' | 'blob' | 'arraybuffer'
  retryCount?: number
}

interface BackendApiFetchDataPartialResponse {
  statusCode?: number
  message?: string
  isClientError?: boolean
  data?: any
}

export const config = {
  api: {
    bodyParser: false,
  },
}
/**
 * Client side action that performs a fetch request to the target API
 * Initially made to be able to use multipart/form-data
 * If you are fetching BE api that doesn't need multipart/form-data, use 'backendApiFetch' instead
 */
export const clientApiFetch = async <T>({
  fullUrl,
  headers = {},
  body,
  method = 'GET',
  session,
  query,
  revalidate,
  cache,
  isMultipart = false,
  responseFormat = 'json',
  retryCount = 0,
}: UseFetchParams): Promise<{ ok: boolean; data?: T & BackendApiFetchDataPartialResponse }> => {
  if (session) {
    headers.Authorization = `Bearer ${session.jwtToken}`
  }

  if (!isMultipart && method === 'POST') {
    body = JSON.stringify(body)
  }

  if (query) {
    fullUrl = `${fullUrl}?${new URLSearchParams(query).toString()}`
  }
  let responseStatus: number | null = null

  try {
    // start fetching here
    const response = await fetch(fullUrl, {
      method,
      headers,
      body,
      cache,
      next: {
        revalidate,
      },
    })

    responseStatus = response.status

    if (responseStatus === 401) {
      return await retryFetch({
        fetchFunctionName: 'clientApiFetch',
        retryCount,
        callFetch: () =>
          clientApiFetch({
            fullUrl,
            headers,
            body,
            method,
            session,
            query,
            revalidate,
            cache,
            isMultipart,
            responseFormat,
            retryCount: retryCount + 1,
          }),
      })
    }

    // some api doesn't return in json format
    // e.g. s3 presigned url returns xml so to avoid error we return text
    const data = responseFormat === 'json' ? await response.json() : await response.text()

    if (!response.ok) {
      console.info(`(${data?.statusCode} - ${data?.error}) ${fullUrl}:`, data?.message)

      return {
        ok: false,
        data,
      }
    }

    return { ok: true, data }
  } catch (error) {
    throw new Error(`Failed to fetch data (${fullUrl}): ${error}`)
  } finally {
    // if response status is 402 meaning payment is required, redirect to resubscribe page
    if (responseStatus === 402) {
      redirect('/checkout/resubscribe?r=1', RedirectType.replace)
    }
  }
}

export const clientApiAxiosFetch = async <T>({
  fullUrl,
  config,
  responseFormat = 'json',
  retryCount = 0,
}: UseAxiosParams): Promise<{
  ok: boolean
  data?: T & BackendApiFetchDataPartialResponse
}> => {
  let responseStatus: number | null = null
  try {
    const locale = await getLocale()
    // start fetching here
    config.headers = config.headers || {}
    config.headers['Accept-Language'] = locale || 'en'
    const response = await axios(config)
    responseStatus = response.status

    if (responseStatus === 401) {
      return await retryFetch({
        fetchFunctionName: 'clientApiAxiosFetch',
        retryCount,
        callFetch: () =>
          clientApiAxiosFetch({
            fullUrl,
            config,
            responseFormat,
            retryCount: retryCount + 1,
          }),
      })
    }

    let data

    switch (responseFormat) {
      case 'json':
      case 'blob':
      case 'arraybuffer':
        data = response.data
        break
      case 'text':
        data = response.request.responseText
        break
    }

    return { ok: true, data }
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Error response from backend (${error.response.status} - ${error.response.statusText}):`,
        error.response.data,
      )
      return {
        ok: false,
        data: { ...error.response.data, statusCode: error.response.data?.statusCode ?? error.response.status },
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error(`No response received for (${fullUrl})`, error.request)
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error(`Error setting up request for (${fullUrl}):`, error.message)
    }

    return {
      ok: false,
      data: { message: `Failed to fetch data (${fullUrl}): ${error.message}`, isClientError: true } as T &
        BackendApiFetchDataPartialResponse,
    }
  } finally {
    // if response status is 402 meaning payment is required, redirect to resubscribe page
    if (responseStatus === 402) {
      redirect('/checkout/resubscribe?r=2', RedirectType.replace)
    }
  }
}
